import { Sizes } from 'components/domain/ProfilingForm/CheckboxButtonChoice';
import { Field, FieldProps, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { BaseFormField } from 'types/form/baseFormField';
import { NumberSelector } from '../NumberSelector';
import { Label } from '../fragments/FieldSet';

interface FormikNumberSelectorProps extends BaseFormField {
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  unitPosition?: 'prefix' | 'suffix';
  variant?: Sizes.sm | Sizes.md | Sizes.lg;
  values?: number[]; // Liste de valeurs prédéfinies
  disabled?: boolean;
  placeholder?: string;
  defaultValue?: number;
}

export const FormikNumberSelector: React.FC<FormikNumberSelectorProps> = ({
  name,
  label,
  required,
  min = 0,
  max = 100,
  step = 1,
  unit,
  unitPosition = 'suffix',
  variant = Sizes.md,
  values,
  disabled,
  placeholder,
  defaultValue
}) => {
  const { setFieldValue, setFieldTouched, values: formValues } = useFormikContext();
  const formValue = (formValues as any)[name];

  // Initialiser la valeur locale uniquement si elle n'existe pas dans Formik
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialisation une seule fois
  useEffect(() => {
    if (!isInitialized) {
      const initialValue = formValue !== undefined ? formValue :
        (defaultValue !== undefined ? defaultValue :
          (values ? values[0] : min));

      if (formValue === undefined) {
        setFieldValue(name, initialValue, false);
      }
      setIsInitialized(true);
    }
  }, [isInitialized, formValue, defaultValue, values, min, name, setFieldValue]);

  const handleChange = (newValue: number) => {
    setFieldTouched(name, true, false);
    setFieldValue(name, newValue, true); // Valider immédiatement
  };

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <div className="space-y-1">
          {label && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
          )}
          <NumberSelector
            id={field.name}
            value={field.value || 0} // Utiliser directement la valeur Formik
            min={min}
            max={max}
            step={step}
            unit={unit}
            unitPosition={unitPosition}
            variant={variant}
            values={values}
            disabled={disabled}
            placeholder={placeholder}
            onChange={handleChange}
          />
          {meta.touched && meta.error && (
            <p className="text-sm text-red-600 mt-1">{meta.error}</p>
          )}
        </div>
      )}
    </Field>
  );
};
